import * as React from "react";
import { cn } from "@/lib/utils";

// Minimal shadcn-new-york button primitive. Phase 1 keeps it dependency-free
// (no class-variance-authority dance) — Phase 3+ swaps in the canonical shadcn
// version via `npx shadcn add button` if/when richer variants are needed.
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
}

const VARIANT: Record<NonNullable<ButtonProps["variant"]>, string> = {
  default: "bg-[var(--talenta-primary)] text-[var(--talenta-primary-foreground)] hover:opacity-90 shadow-[var(--talenta-shadow-primary)]",
  outline: "border border-[var(--talenta-border)] bg-transparent hover:bg-[var(--talenta-surface)]",
  ghost: "bg-transparent hover:bg-[var(--talenta-surface)]",
};

const SIZE: Record<NonNullable<ButtonProps["size"]>, string> = {
  sm: "h-9 px-3 text-sm",
  md: "h-11 px-4 text-sm",
  lg: "h-12 px-5 text-base",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "md", type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2",
        "disabled:opacity-50 disabled:pointer-events-none",
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = "Button";
